import React, { useMemo, useState, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Stack,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  LinearProgress,
  Alert,
  Avatar,
  Tabs,
  Tab,
  Checkbox,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import PreviewIcon from "@mui/icons-material/Preview";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import ChecklistIcon from "@mui/icons-material/Checklist";
import ArticleIcon from "@mui/icons-material/Article";
import { styled } from "@mui/material/styles";
import * as XLSX from "xlsx";

/**
 * RcsCampaignBuilder
 * A polished MUI component to build RCS campaigns with:
 * - Template selection (with variables like {{name}})
 * - Sender/Phone number selection
 * - Upload contacts via Excel/CSV (xlsx/csv)
 * - Live personalization preview & campaign summary
 *
 * Notes:
 * - Expects `templates` and `senders` (phone numbers) as optional props; falls back to demo data.
 * - `onSubmit` is called with the composed payload when user clicks "Create Campaign".
 */

const DropZone = styled("label")(({ theme, dragging }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
  gap: 8,
  border: `1px dashed ${
    dragging ? theme.palette.primary.main : theme.palette.divider
  }`,
  borderRadius: 16,
  padding: 24,
  cursor: "pointer",
  transition: "border-color 0.2s ease, background 0.2s ease",
  background: dragging
    ? theme.palette.action.hover
    : theme.palette.background.paper,
}));

const TwoLineEllipsis = styled(Box)(({ theme }) => ({
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
}));

const SmallStat = ({ label, value }) => (
  <Stack spacing={0.5} alignItems="center">
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="subtitle1" fontWeight={700}>
      {value}
    </Typography>
  </Stack>
);

const demoTemplates = [
  {
    id: "t1",
    name: "Order Update",
    content:
      "Hi {{name}}, your order #{{orderId}} has been shipped via {{courier}}. Track: {{trackingLink}}",
  },
  {
    id: "t2",
    name: "Event Reminder",
    content:
      "Hello {{name}}, this is a reminder for {{eventName}} on {{eventDate}} at {{eventTime}}.",
  },
  {
    id: "t3",
    name: "Promo Blast",
    content:
      "Hey {{name}}! Enjoy {{discount}} off on {{product}}. Use code {{coupon}} by {{expiry}}.",
  },
];

const demoSenders = [
  { id: "s1", label: "+91 90000 12345" },
  { id: "s2", label: "+91 90000 54321" },
  { id: "s3", label: "+91 98888 00000" },
];

const steps = [
  "Select template",
  "Select phone number",
  "Upload contacts",
  "Preview & create",
];

const phoneFromRow = (row) => {
  // Try common headers
  const keys = Object.keys(row || {});
  const phoneKey = keys.find((k) => /phone|mobile|msisdn|contact/i.test(k));
  const nameKey = keys.find((k) => /name|full.?name|first.?name/i.test(k));
  return {
    phone: (phoneKey ? String(row[phoneKey]) : "").replace(/[^0-9+]/g, ""),
    name: nameKey ? String(row[nameKey]) : "",
  };
};

const sanitizePhone = (phone) => {
  if (!phone) return "";
  let p = String(phone).trim();
  // Normalize Indian numbers to +91 where applicable
  if (/^0[0-9]{10}$/.test(p)) p = p.slice(1); // remove leading 0
  if (/^[0-9]{10}$/.test(p)) p = "+91" + p;
  if (!p.startsWith("+")) p = "+" + p.replace(/[^0-9]/g, "");
  return p;
};

const detectVariables = (content) => {
  const re = /{{\s*([a-zA-Z0-9_\.]+)\s*}}/g;
  const vars = new Set();
  let m;
  while ((m = re.exec(content))) vars.add(m[1]);
  return Array.from(vars);
};

const renderTemplate = (content, row = {}) => {
  return content.replace(/{{\s*([a-zA-Z0-9_\.]+)\s*}}/g, (_, k) => {
    const v = row[k];
    return v === undefined || v === null || v === "" ? `{${k}}` : String(v);
  });
};

export default function RcsCampaignBuilder({
  templates = demoTemplates,
  senders = demoSenders,
  onSubmit,
}) {
  const [active, setActive] = useState(0);
  const [selectedTemplateId, setSelectedTemplateId] = useState(
    templates?.[0]?.id || ""
  );
  const [templateContent, setTemplateContent] = useState(
    templates?.[0]?.content || ""
  );
  const [selectedSenderId, setSelectedSenderId] = useState(
    senders?.[0]?.id || ""
  );
  const [contacts, setContacts] = useState([]); // raw rows
  const [validContacts, setValidContacts] = useState([]); // { phone, ...row }
  const [invalidContacts, setInvalidContacts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [tab, setTab] = useState(0);
  const fileInputRef = useRef(null);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === selectedTemplateId) || null,
    [selectedTemplateId, templates]
  );

  const selectedSender = useMemo(
    () => senders.find((s) => s.id === selectedSenderId) || null,
    [selectedSenderId, senders]
  );

  const templateVars = useMemo(
    () => detectVariables(templateContent),
    [templateContent]
  );

  const stats = useMemo(() => {
    const total = contacts.length;
    const valid = validContacts.length;
    const invalid = invalidContacts.length;
    const unique = new Set(validContacts.map((c) => c.phone)).size;
    return { total, valid, invalid, unique };
  }, [contacts, validContacts, invalidContacts]);

  const sampleRow = validContacts[0] || invalidContacts[0] || {};
  const samplePreview = renderTemplate(templateContent, sampleRow);

  const handleTemplateChange = (id) => {
    setSelectedTemplateId(id);
    const t = templates.find((x) => x.id === id);
    setTemplateContent(t?.content || "");
  };

  const handleFile = async (file) => {
    setUploading(true);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });

      // Normalize rows: ensure phone & name fields and sanitize phone
      const normalized = rows.map((r) => {
        const { phone, name } = phoneFromRow(r);
        const p = sanitizePhone(phone);
        return { ...r, phone: p, name };
      });

      // Partition valid/invalid; dedupe on phone keeping first occurrence
      const seen = new Set();
      const val = [];
      const inval = [];
      for (const r of normalized) {
        const p = r.phone;
        const looksValid = /^\+[0-9]{8,15}$/.test(p); // basic E.164-ish
        if (!looksValid || seen.has(p)) {
          inval.push(r);
        } else {
          seen.add(p);
          val.push(r);
        }
      }
      setContacts(normalized);
      setValidContacts(val);
      setInvalidContacts(inval);
      setActive(3); // jump to preview after upload
      setTab(0);
    } catch (e) {
      console.error(e);
      alert("Failed to parse file. Ensure it's a valid .xlsx or .csv");
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  const handlePickFile = (e) => {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
    if (fileInputRef.current) fileInputRef.current.value = ""; // reset
  };

  const downloadSample = () => {
    const ws = XLSX.utils.json_to_sheet([
      {
        name: "Ratul",
        phone: "+919876543210",
        orderId: "A123",
        courier: "BlueDart",
        trackingLink: "https://example.com/t/A123",
      },
      {
        name: "Shreya",
        phone: "+919812345678",
        orderId: "B456",
        courier: "Delhivery",
        trackingLink: "https://example.com/t/B456",
      },
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "contacts");
    const wbout = XLSX.write(wb, { type: "array", bookType: "xlsx" });
    const blob = new Blob([wbout], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "rcs-contacts-sample.xlsx";
    a.click();
    URL.revokeObjectURL(url);
  };

  const payload = useMemo(
    () => ({
      templateId: selectedTemplate?.id,
      senderId: selectedSender?.id,
      templateContent,
      contacts: validContacts, // already deduped by phone
    }),
    [selectedTemplate, selectedSender, templateContent, validContacts]
  );

  const canSubmit =
    payload.templateId && payload.senderId && payload.contacts.length > 0;

  const handleCreate = () => {
    if (!canSubmit) return;
    if (onSubmit) onSubmit(payload);
    alert("Campaign created! Check console for payload.");
    console.log("RCS Campaign Payload", payload);
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Stack spacing={2}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar variant="rounded">
              <ArticleIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                RCS Campaign Builder
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Create, personalize, and preview your RCS campaign.
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <SmallStat label="Total" value={stats.total} />
            <Divider flexItem orientation="vertical" />
            <SmallStat label="Valid" value={stats.valid} />
            <Divider flexItem orientation="vertical" />
            <SmallStat label="Invalid/Dup" value={stats.invalid} />
            <Divider flexItem orientation="vertical" />
            <SmallStat label="Unique send" value={stats.unique} />
            <Button
              variant="contained"
              startIcon={<ChecklistIcon />}
              disabled={!canSubmit}
              onClick={handleCreate}
            >
              Create Campaign
            </Button>
          </Stack>
        </Stack>

        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Stepper activeStep={active} alternativeLabel>
              {steps.map((label, i) => (
                <Step
                  key={label}
                  onClick={() => setActive(i)}
                  sx={{ cursor: "pointer" }}
                >
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </CardContent>
        </Card>

        <Stack direction={{ xs: "column", lg: "row" }} spacing={2}>
          {/* Left column: controls */}
          <Stack spacing={2} flex={1}>
            {/* Step 1: Template selection */}
            <Card
              sx={{
                borderRadius: 3,
                outline:
                  active === 0
                    ? (theme) => `2px solid ${theme.palette.primary.main}`
                    : "none",
              }}
            >
              <CardHeader
                title={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <ArticleIcon />
                    <Typography variant="subtitle1" fontWeight={700}>
                      1. Select template
                    </Typography>
                  </Stack>
                }
              />
              <CardContent>
                <Stack spacing={2}>
                  <FormControl fullWidth>
                    <InputLabel id="tpl-label">Template</InputLabel>
                    <Select
                      labelId="tpl-label"
                      label="Template"
                      value={selectedTemplateId}
                      onChange={(e) => handleTemplateChange(e.target.value)}
                    >
                      {templates.map((t) => (
                        <MenuItem key={t.id} value={t.id}>
                          <TwoLineEllipsis>
                            {t.name} — {t.content}
                          </TwoLineEllipsis>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    label="Message content"
                    minRows={4}
                    maxRows={8}
                    multiline
                    value={templateContent}
                    onChange={(e) => setTemplateContent(e.target.value)}
                    helperText={
                      templateVars.length
                        ? `Variables detected: ${templateVars.join(", ")}`
                        : "Tip: Use {{name}} or {{orderId}} etc."
                    }
                  />

                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                  >
                    {templateVars.map((v) => (
                      <Chip key={v} size="small" label={`{{${v}}}`} />
                    ))}
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            {/* Step 2: Sender selection */}
            <Card
              sx={{
                borderRadius: 3,
                outline:
                  active === 1
                    ? (theme) => `2px solid ${theme.palette.primary.main}`
                    : "none",
              }}
            >
              <CardHeader
                title={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <PhoneIphoneIcon />
                    <Typography variant="subtitle1" fontWeight={700}>
                      2. Select phone number
                    </Typography>
                  </Stack>
                }
              />
              <CardContent>
                <Stack spacing={2}>
                  <FormControl fullWidth>
                    <InputLabel id="sender-label">Sender number</InputLabel>
                    <Select
                      labelId="sender-label"
                      label="Sender number"
                      value={selectedSenderId}
                      onChange={(e) => setSelectedSenderId(e.target.value)}
                    >
                      {senders.map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                          {s.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Checkbox size="small" defaultChecked />
                    <Typography variant="body2" color="text.secondary">
                      Allow fallback to SMS for unsupported devices
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            {/* Step 3: Upload contacts */}
            <Card
              sx={{
                borderRadius: 3,
                outline:
                  active === 2
                    ? (theme) => `2px solid ${theme.palette.primary.main}`
                    : "none",
              }}
            >
              <CardHeader
                title={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <UploadFileIcon />
                    <Typography variant="subtitle1" fontWeight={700}>
                      3. Upload contacts
                    </Typography>
                  </Stack>
                }
              />
              <CardContent>
                <Stack spacing={2}>
                  <DropZone
                    onDragOver={(e) => {
                      e.preventDefault();
                    }}
                    onDrop={handleDrop}
                  >
                    <input
                      hidden
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={handlePickFile}
                      ref={fileInputRef}
                    />
                    <UploadFileIcon />
                    <Typography variant="body1">
                      Drop your .xlsx/.csv here or click to browse
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Accepted headers include: name, phone/mobile, orderId,
                      etc.
                    </Typography>
                    <Button
                      variant="text"
                      startIcon={<DownloadIcon />}
                      onClick={(e) => {
                        e.preventDefault();
                        downloadSample();
                      }}
                    >
                      Download sample
                    </Button>
                  </DropZone>
                  {uploading && <LinearProgress />}

                  {invalidContacts.length > 0 && (
                    <Alert severity="warning" variant="outlined">
                      {invalidContacts.length} rows skipped due to
                      invalid/duplicate phone numbers.
                    </Alert>
                  )}

                  {validContacts.length > 0 && (
                    <Box
                      sx={{
                        maxHeight: 260,
                        overflow: "auto",
                        borderRadius: 2,
                        border: (t) => `1px solid ${t.palette.divider}`,
                      }}
                    >
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "220px 220px 1fr 1fr",
                          fontSize: 13,
                          fontWeight: 600,
                          px: 2,
                          py: 1,
                          position: "sticky",
                          top: 0,
                          bgcolor: "background.paper",
                          borderBottom: (t) => `1px solid ${t.palette.divider}`,
                        }}
                      >
                        <Box>Name</Box>
                        <Box>Phone</Box>
                        <Box>Col 3</Box>
                        <Box>Col 4</Box>
                      </Box>
                      {validContacts.map((r, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "220px 220px 1fr 1fr",
                            px: 2,
                            py: 1.25,
                            borderBottom: (t) =>
                              `1px dashed ${t.palette.divider}`,
                          }}
                        >
                          <TwoLineEllipsis>{r.name || "—"}</TwoLineEllipsis>
                          <Box>{r.phone}</Box>
                          <TwoLineEllipsis>
                            {r.orderId || r.eventName || r.product || ""}
                          </TwoLineEllipsis>
                          <TwoLineEllipsis>
                            {r.courier || r.eventDate || r.discount || ""}
                          </TwoLineEllipsis>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Stack>

          {/* Right column: live preview */}
          <Card sx={{ borderRadius: 3, width: { lg: 420 }, flexShrink: 0 }}>
            <CardHeader
              title={
                <Stack direction="row" spacing={1} alignItems="center">
                  <PreviewIcon />
                  <Typography variant="subtitle1" fontWeight={700}>
                    4. Preview & create
                  </Typography>
                </Stack>
              }
            />
            <CardContent>
              <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
                <Tab
                  label={`Sample (${
                    validContacts.length ? "from data" : "empty"
                  })`}
                />
                <Tab label="Raw template" />
                <Tab label="Summary" />
              </Tabs>

              {tab === 0 && (
                <Stack spacing={1.5}>
                  <Typography variant="caption" color="text.secondary">
                    Personalized preview
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: (t) => `1px solid ${t.palette.divider}`,
                      bgcolor: "background.default",
                      minHeight: 120,
                    }}
                  >
                    <Typography whiteSpace="pre-wrap">
                      {samplePreview}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip
                      size="small"
                      label={`Characters: ${samplePreview.length}`}
                    />
                    <Chip
                      size="small"
                      label={`Variables: ${templateVars.length}`}
                    />
                    <Chip
                      size="small"
                      label={`Recipient example: ${sampleRow.phone || "—"}`}
                    />
                  </Stack>
                </Stack>
              )}

              {tab === 1 && (
                <Stack spacing={1.5}>
                  <Typography variant="caption" color="text.secondary">
                    Raw template
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: (t) => `1px solid ${t.palette.divider}`,
                      bgcolor: "background.default",
                      minHeight: 120,
                    }}
                  >
                    <Typography whiteSpace="pre-wrap">
                      {templateContent}
                    </Typography>
                  </Box>
                </Stack>
              )}

              {tab === 2 && (
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip
                      label={`Template: ${selectedTemplate?.name || "—"}`}
                    />
                    <Chip label={`Sender: ${selectedSender?.label || "—"}`} />
                    <Chip label={`Unique recipients: ${stats.unique}`} />
                    <Chip label={`Skipped: ${invalidContacts.length}`} />
                  </Stack>
                  <Divider />
                  <Typography variant="body2" color="text.secondary">
                    Make sure your template and variables match the uploaded
                    columns.
                  </Typography>
                </Stack>
              )}

              <Divider sx={{ my: 2 }} />
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Tooltip title="Reset current selection">
                  <span>
                    <IconButton
                      onClick={() => {
                        setContacts([]);
                        setValidContacts([]);
                        setInvalidContacts([]);
                        setTab(0);
                      }}
                      disabled={!contacts.length}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleCreate}
                  disabled={!canSubmit}
                >
                  Create Campaign
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Stack>

        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography variant="caption" color="text.secondary">
              Tips: Use headers like <strong>name</strong> and{" "}
              <strong>phone</strong> in your sheet. Variables inside the
              template like <strong>{`{{name}}`}</strong> will automatically map
              if the column exists.
            </Typography>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}
